import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { UserDetails, Agency } from '../types/User';
import { useDocumentData } from '../hooks/useDocumentData';
import { useUserDocuments } from '../hooks/useUserDocuments';
import { useDocumentMap } from '../hooks/useDocumentMap';
import { Document, DocumentWithType, DocumentCategory, DocumentField, DocumentSubcategory, DocumentType } from '../types/Document';
import { OverallCompletionData } from '../utils/completionCalculations';
import { useEnhancedAuth } from './EnhancedAuthContext';

interface DocumentContextType {
  // selection & scope
  selectedUser: UserDetails | null;
  setSelectedUser: (user: UserDetails | null) => void;
  agency: Agency | null;
  
  // structural data
  categories: DocumentCategory[];
  categoriesWithStats: any[];
  subcategories: DocumentSubcategory[];
  documentTypes: DocumentType[];
  documentFields: DocumentField[];
  getSubcategoriesForCategory: (categoryId: string) => DocumentSubcategory[];
  getDocumentTypesForSubcategory: (subcategoryId: string) => DocumentType[];
  getDocumentTypesForCategory: (categoryId: string) => DocumentType[];
  getFieldsForSubcategory: (subcategoryId: string) => DocumentField[];
  getFieldsForCategory: (categoryId: string) => DocumentField[];
  
  // user-specific data
  userDocuments: Document[];
  userFieldValues: { [key: string]: any };
  saveFieldValues: (values: { [key: string]: any }) => Promise<{ success: boolean; error: string | null }>;
  fetchFieldValues: (fieldIds: string[]) => Promise<void> | void;
  
  // Local document management for optimistic updates
  addDocument: (newDoc: Document) => void;
  removeDocument: (documentId: string) => void;
  updateDocument: (documentId: string, updates: Partial<Document>) => void;
  documentUpload: (formData: FormData) => Promise<{ success: boolean; error?: string; newDocument?: any }>;
  documentRemove: (docID: string) => Promise<{ success: boolean; error?: string }>;
  
  // stats
  completionStats: OverallCompletionData | null;
  overallStats: {
    totalItems: number;
    completedItems: number;
    missingItems: number;
    expiredItems: number;
    completionRate: number;
  };

  // loading & errors
  structuralLoading: boolean;
  userDataLoading: boolean;
  loading: boolean;
  error: string | null;

  // refresh
  refreshUserData: () => void | Promise<void>;
  refreshStructuralData: () => void | Promise<void>;

  // utilities
  documentDownload: (presignedURL: string) => Promise<void>;
  fetchRecentDocs: (userID: string) => Promise<DocumentWithType[]>;
  fetchUserReminders: (userID: string) => Promise<any[]>;
  fetchAgencyReminders: (agencyID: string) => Promise<any[]>;
  sendUserDocumentReminders: (documentTypeID: string, userID: string, managerID: string, text: string) => Promise<{ success: boolean; error?: string }>;
  sendUserFieldReminders: (fieldTypeID: string, userID: string, managerID: string, text: string) => Promise<{ success: boolean; error?: string }>;

  // Document Highlighting from reminders
  highlightedDocumentId: string | null;
  setHighlightedDocumentId: (docId: string | null) => void;
  highlightedFieldId: string | null;
  setHighlightedFieldId: (fieldId: string | null) => void;
  expandedCategoryId: string | null;
  setExpandedCategoryId: (catId: string | null) => void;
  expandedSubcategoryId: string | null;
  setExpandedSubcategoryId: (subcatId: string | null) => void;
  userReminders?: any;
  agencyReminders?: { documentReminders: any[]; fieldReminders: any[] } | null;
  removeUserDocumentReminders: (documentTypeID: string, userID: string) => Promise<{ success: boolean; error?: string }>;
  removeUserFieldReminders: (fieldTypeID: string, userID: string) => Promise<{ success: boolean; error?: string }>;
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

interface DocumentProviderProps {
  children: ReactNode;
  initialSelectedUser?: UserDetails | null;
}

export const DocumentProvider: React.FC<DocumentProviderProps> = ({
  children,
  initialSelectedUser = null,
}) => {
  const { userDetails } = useEnhancedAuth();

  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(initialSelectedUser);

  // Extract agency to avoid accessing nested property in dependencies
  const agency = userDetails?.agency || null;

  // Auto-set selectedUser to current user if not already set
  React.useEffect(() => {
    if (!selectedUser && userDetails) {
      setSelectedUser(userDetails);
    }
  }, [selectedUser, userDetails]);

  // Use hooks inside the context - use authenticated user for structural data, not selected user
  const map = useDocumentMap(userDetails);
  const userDocs = useUserDocuments(selectedUser, agency, map.documentFields);

  // Compose derived data
  const data = useDocumentData({
    categories: map.categories,
    subcategories: map.subcategories,
    documentTypes: map.documentTypes,
    documentFields: map.documentFields,
    userDocuments: userDocs.userDocuments,
    userFieldValues: userDocs.userFieldValues,
    selectedUser,
  });

  // State for document highlighting and expansion
  const [highlightedDocumentId, setHighlightedDocumentId] = useState<string | null>(null);
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(null);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [expandedSubcategoryId, setExpandedSubcategoryId] = useState<string | null>(null);

  const contextValue: DocumentContextType = useMemo(() => ({
    selectedUser,
    setSelectedUser,
    agency,

    // structural
    categories: map.categories,
    categoriesWithStats: data.categoriesWithStats,
    subcategories: map.subcategories,
    documentTypes: map.documentTypes,
    documentFields: map.documentFields,
    getSubcategoriesForCategory: map.getSubcategoriesForCategory,
    getDocumentTypesForSubcategory: map.getDocumentTypesForSubcategory,
    getDocumentTypesForCategory: map.getDocumentTypesForCategory,
    getFieldsForSubcategory: map.getFieldsForSubcategory,
    getFieldsForCategory: map.getFieldsForCategory,

    // user-specific
    userDocuments: userDocs.userDocuments,
    userFieldValues: userDocs.userFieldValues,
    saveFieldValues: userDocs.saveFieldValues,
    fetchFieldValues: userDocs.fetchFieldValues,

    // Local document management
    addDocument: userDocs.addDocument,
    removeDocument: userDocs.removeDocument,
    updateDocument: userDocs.updateDocument,
    documentUpload: userDocs.documentUpload,
    documentRemove: userDocs.documentRemove,

    // stats
    completionStats: data.completionStats,
    overallStats: data.overallStats,

    // loading & errors
    structuralLoading: map.loading,
    userDataLoading: userDocs.loading,
    loading: map.loading || userDocs.loading,
    error: map.error || userDocs.error,

    // refresh
    refreshUserData: userDocs.refetch,
    refreshStructuralData: map.refetch,

    // utilities
    documentDownload: userDocs.documentDownload,
    fetchRecentDocs: userDocs.fetchRecentDocs,
    fetchUserReminders: userDocs.fetchUserReminders,
    fetchAgencyReminders: userDocs.fetchAgencyReminders,
    sendUserDocumentReminders: userDocs.sendUserDocumentReminders,
    sendUserFieldReminders: userDocs.sendUserFieldReminders,

    // Document Highlighting from reminders
    highlightedDocumentId: highlightedDocumentId,
    setHighlightedDocumentId: setHighlightedDocumentId,
    highlightedFieldId: highlightedFieldId,
    setHighlightedFieldId: setHighlightedFieldId,
    expandedCategoryId: expandedCategoryId,
    setExpandedCategoryId: setExpandedCategoryId,
    expandedSubcategoryId: expandedSubcategoryId,
    setExpandedSubcategoryId: setExpandedSubcategoryId,
    userReminders: userDocs.userReminders,
    agencyReminders: userDocs.agencyReminders,
    removeUserDocumentReminders: userDocs.removeUserDocumentReminders,
    removeUserFieldReminders: userDocs.removeUserFieldReminders,
  }), [
    selectedUser,
    agency,
    map.categories,
    data.categoriesWithStats,
    map.subcategories,
    map.documentTypes,
    map.documentFields,
    map.getSubcategoriesForCategory,
    map.getDocumentTypesForSubcategory,
    map.getDocumentTypesForCategory,
    map.getFieldsForSubcategory,
    userDocs.userDocuments,
    userDocs.userFieldValues,
    userDocs.saveFieldValues,
    userDocs.fetchFieldValues,
    userDocs.addDocument,
    userDocs.removeDocument,
    userDocs.updateDocument,
    userDocs.documentUpload,
    userDocs.documentRemove,
    data.completionStats,
    data.overallStats,
    map.loading,
    userDocs.loading,
    map.error,
    userDocs.error,
    userDocs.refetch,
    map.refetch,
    userDocs.documentDownload,
    userDocs.fetchRecentDocs,
    userDocs.fetchUserReminders,
    userDocs.fetchAgencyReminders,
    userDocs.sendUserDocumentReminders,
    userDocs.sendUserFieldReminders,
    highlightedDocumentId,
    highlightedFieldId,
    expandedCategoryId,
    expandedSubcategoryId,
    userDocs.userReminders,
    userDocs.agencyReminders,
    userDocs.removeUserDocumentReminders,
    userDocs.removeUserFieldReminders,
  ]);

  return (
    <DocumentContext.Provider value={contextValue}>
      {children}
    </DocumentContext.Provider>
  );
};

export const useDocumentContext = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocumentContext must be used within a DocumentProvider');
  }
  return context;
};
